import React, { useCallback, useMemo, useState } from 'react';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useHistory } from 'react-router-dom';
import AuthFooter from '@/common/components/AuthFooter/AuthFooter';
import AuthHeader from '@/common/components/AuthHeader/AuthHeader';
import passwordResetImg from '@/common/assets/password-reset-true.svg';
import { SetPassword } from '@/common/containers/Forms/index';
import { useQuery } from '@/common/hooks/index';
import { routes } from '@/app/routing/index';
import { useConfirmPasswordResetMutation } from '@/entities/user';
import { Notification } from '@/common/utils/index';
import newPasswordStyles from '@/modules/new-password/new-password.styles';
import { useTranslation, Trans } from 'react-i18next';
import CopyrightSup from '@/common/components/CopyrightSup';

const NewPasswordModule = () => {
  const { t } = useTranslation();
  const query = useQuery();
  const history = useHistory();
  const [submitted, setSubmitted] = useState(0);
  const confirmPasswordResetMutation = useConfirmPasswordResetMutation();
  const copyrightSup = <CopyrightSup />;

  const TEXT = useMemo(() => {
    return [
      {
        title: t('Set new password'),
        label: (
          <Trans
            i18nKey="Enter a new password for signing in to PAEM After Care<copyrightSup/>"
            components={{ copyrightSup }}
          />
        ),
      },
      {
        title: t('Password reset'),
        label: t(
          'Your password has been successfully reset. Click below to login into Praedias'
        ),
      },
    ][submitted];
  }, [copyrightSup, submitted, t]);

  const onSubmit = useCallback(
    async ({ password }) => {
      try {
        const email = query.get('em')?.replace(/ /g, '+');
        await confirmPasswordResetMutation.mutateAsync({
          email,
          token: query.get('tk'),
          password,
        });
        setSubmitted(1);
      } catch (err) {
        Notification.error(err?.message);
        throw err;
      }
    },
    [confirmPasswordResetMutation, query]
  );

  const onLogin = useCallback(() => {
    history.push(routes.login);
  }, [history]);

  return (
    <Box component="section" sx={newPasswordStyles.container}>
      <AuthHeader />
      <Stack sx={newPasswordStyles.formWrapper}>
        {!!submitted && (
          <Box
            component="img"
            sx={newPasswordStyles.successIcon}
            src={passwordResetImg}
            alt={t('Password reset')}
          />
        )}
        <Typography variant="h2" textAlign="center">
          {TEXT.title}
        </Typography>
        <Typography variant="body1" textAlign="center" sx={{ mt: 4, mb: 10 }}>
          {TEXT.label}
        </Typography>
        {!submitted ? (
          <SetPassword
            onSubmit={onSubmit}
            submitText={t('Reset Password')}
            isPending={confirmPasswordResetMutation.isPending}
          />
        ) : (
          <Button
            type="submit"
            variant="contained"
            size="medium"
            onClick={onLogin}
            sx={{ mx: 'auto' }}
          >
            {t('Login')}
          </Button>
        )}
      </Stack>
      <AuthFooter />
    </Box>
  );
};

export default NewPasswordModule;
